/**
 * Picks which Flagsmith flags gate an item.
 *
 * The flags come from the BU's own environment, so a BU without a key gets an empty list and a
 * pointer to fix that — never a free-text field. A flag typed by hand that does not exist hides
 * the item from everyone, silently.
 *
 * @module Pages/BUs/WhatsNew
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton } from '@group-link-one/gl-ui/utilities';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { TenantFlag } from '../../../Hooks/useTenantFlags';
import { maskEnvironmentKey } from '../../../Utils/maskEnvironmentKey';

const popoverStyle = css`
  position: absolute;
  z-index: var(--z-drawer);
  top: calc(100% + 6px);
  left: 0;
  width: min(340px, 90vw);
  background: var(--color-surface);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-m);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
`;

const searchWrapStyle = css`
  padding: var(--space-s);
  border-bottom: 1px solid var(--color-divider);
`;

const searchStyle = css`
  width: 100%;
  height: 34px;
  padding: 0 var(--space-s);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-control);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  background: var(--color-surface);
  color: var(--color-text);

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const listStyle = css`
  max-height: 240px;
  overflow-y: auto;
  padding: var(--space-s);
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  padding: 6px var(--space-s);
  border-radius: var(--radius-sm);
  cursor: pointer;

  &:hover {
    background: var(--color-surface-hover);
  }
`;

const rowTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const flagNameStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text);
`;

const flagDescriptionStyle = css`
  font-size: 10px;
  color: var(--color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const emptyStyle = css`
  padding: var(--space-m);
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const footerStyle = css`
  padding: var(--space-s) var(--space-m);
  border-top: 1px solid var(--color-divider);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: var(--color-text-muted);
`;

const keyStyle = css`
  font-family: var(--font-mono);
`;

export interface PrivilegesPickerProps {
  selected: string[];
  flags: TenantFlag[];
  isLoading: boolean;
  isMissingKey: boolean;
  environmentKey?: string | null;
  onToggle: (flagName: string) => void;
  onClose: () => void;
  onOpenBU: () => void;
}

export const PrivilegesPicker = ({
  selected,
  flags,
  isLoading,
  isMissingKey,
  environmentKey,
  onToggle,
  onClose,
  onOpenBU,
}: PrivilegesPickerProps): JSX.Element => {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!popoverRef.current?.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return flags;
    return flags.filter(flag => flag.name.toLowerCase().includes(term));
  }, [flags, query]);

  return (
    <div ref={popoverRef} className={popoverStyle} role='dialog' aria-label='Escolher privileges'>
      <div className={searchWrapStyle}>
        <input
          ref={searchRef}
          className={searchStyle}
          placeholder='Buscar flag'
          value={query}
          disabled={isMissingKey}
          onChange={event => {
            setQuery(event.target.value);
          }}
        />
      </div>

      {isMissingKey ? (
        <div className={emptyStyle}>
          <span>
            Esta BU não tem key de ambiente do Flagsmith cadastrada, então não há flags para listar.
          </span>
          <UTButton fitContent variant='tertiary' size='small' text='Abrir BU' onClick={onOpenBU} />
        </div>
      ) : isLoading ? (
        <span className={emptyStyle}>Carregando flags…</span>
      ) : results.length === 0 ? (
        <span className={emptyStyle}>Nenhuma flag com esse nome.</span>
      ) : (
        <div className={listStyle}>
          {results.map(flag => (
            <label key={flag.name} className={rowStyle}>
              <input
                type='checkbox'
                checked={selected.includes(flag.name)}
                onChange={() => {
                  onToggle(flag.name);
                }}
              />
              <div className={rowTextStyle}>
                <span className={flagNameStyle}>{flag.name}</span>
                {flag.description ? (
                  <span className={flagDescriptionStyle}>{flag.description}</span>
                ) : null}
              </div>
            </label>
          ))}
        </div>
      )}

      <div className={footerStyle}>
        <GLIcon variant='Misc.Broadcast' size={12} fill='var(--color-text-muted)' />
        {isMissingKey ? (
          <span>sem key de flags</span>
        ) : (
          <>
            <span>{flags.length} flags</span>
            <span className={keyStyle}>{maskEnvironmentKey(environmentKey ?? '')}</span>
          </>
        )}
      </div>
    </div>
  );
};
